import React, { FC, Fragment } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import dayjs, { Dayjs } from 'dayjs';
import './CourierInvoiceTable.scss';
import { CourierInvoiceLine } from './hooks';
import { showDeliveryRateColumn, showIDChecksColumn } from './utils';

const CourierInvoiceTable: FC<{
  invoiceLines: CourierInvoiceLine[];
  reportStartDate: Dayjs;
}> = ({ invoiceLines, reportStartDate }) => {
  const displayKroner = (n: number): string => (n === 0 ? '-' : String(n));

  const shouldShowIDChecksColumn = showIDChecksColumn(invoiceLines);
  const shouldShowDeliveryRateColumn = showDeliveryRateColumn(reportStartDate);
  const colspanLength = shouldShowDeliveryRateColumn
    ? shouldShowIDChecksColumn
      ? 15
      : 14
    : 13;

  return (
    <div className="courierInvoiceTable">
      <Table>
        <TableHead>
          <TableRow className="courierInvoiceTable__normalRows">
            <TableCell className="courierInvoiceTable__alignedLeft">
              DATE
            </TableCell>
            <TableCell>DELIVERIES</TableCell>
            {shouldShowDeliveryRateColumn && (
              <TableCell>DELIVERY RATE</TableCell>
            )}
            <TableCell>CONSOLIDATED</TableCell>
            <TableCell>REGION</TableCell>
            <TableCell>VEHICLE</TableCell>
            <TableCell>KM</TableCell>
            <TableCell>SORTING</TableCell>
            <TableCell>INBOUND</TableCell>
            <TableCell>BACKUP</TableCell>
            <TableCell>OTHER</TableCell>
            <TableCell>PRICE ADJ. RATE</TableCell>
            <TableCell>PRICE ADJ.</TableCell>
            {shouldShowIDChecksColumn && <TableCell>ID CHECKS</TableCell>}
            <TableCell className="courierInvoiceTable__alignedRight">
              TOTAL EXCL. VAT
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoiceLines.map((invoiceLine, i) => {
            const deliveries = Object.values(invoiceLine.deliveries).reduce(
              (acc, details) => acc + details.count,
              0
            );
            return invoiceLine.total !== 0 ? (
              <Fragment key={i}>
                <TableRow className="courierInvoiceTable__normalRows">
                  <TableCell className="courierInvoiceTable__alignedLeft">
                    {dayjs(new Date(invoiceLine.date)).format('DD.MM.YYYY')}
                  </TableCell>
                  <TableCell>{deliveries}</TableCell>
                  {shouldShowDeliveryRateColumn && (
                    <TableCell>
                      {invoiceLine.deliveries[invoiceLine.vehicle]?.rate ?? '-'}
                    </TableCell>
                  )}
                  <TableCell>{invoiceLine.consolidated.count}</TableCell>
                  <TableCell>{invoiceLine.region}</TableCell>
                  <TableCell>{invoiceLine.vehicle ?? '-'}</TableCell>
                  <TableCell>{invoiceLine.km.count ?? 0}</TableCell>
                  <TableCell>
                    {displayKroner(invoiceLine.sorting.total)}
                  </TableCell>
                  <TableCell>
                    {displayKroner(invoiceLine.inbound.total)}
                  </TableCell>
                  <TableCell>
                    {displayKroner(invoiceLine.backup.total)}
                  </TableCell>
                  <TableCell>
                    {displayKroner(invoiceLine.extras.amount)}
                  </TableCell>
                  <TableCell>{invoiceLine.minComp.threshold ?? '-'}</TableCell>
                  <TableCell>{invoiceLine.minComp.amount}</TableCell>
                  {shouldShowIDChecksColumn && (
                    <TableCell>{invoiceLine.idVerifications.count}</TableCell>
                  )}
                  <TableCell className="courierInvoiceTable__alignedRight">
                    {invoiceLine.total}
                  </TableCell>
                </TableRow>
                {invoiceLine.extras.descriptions.map((description, j) => {
                  const separator = ' - ';
                  // No regex due to safari regex lookbehind constraint
                  // Keeps everything after the first hyphen
                  const extraDescription =
                    description.split(separator)?.slice(1)?.join(separator) ??
                    'No description';

                  return (
                    <TableRow key={j}>
                      <TableCell
                        colSpan={colspanLength}
                        className="courierInvoiceTable__extrasDescription"
                      >
                        {`Other: ${extraDescription}`}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            ) : null;
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default CourierInvoiceTable;
